import { createContext, useContext, useMemo } from 'react';

// Brand configurations
const BRANDS = {
  strider: {
    id: 'strider',
    name: 'STRIDER',
    tagline: "Let's expand where the world can succeed.",
    logo: '/strider-logo-padded.png',
    domain: 'strider.work',
    email: 'info@strider.work',
    social: {
      instagram: 'https://www.instagram.com/strider.work/',
      facebook: 'https://www.facebook.com/strider.work',
    },
    company: {
      name: 'BOLDHORN PRESS LTD',
      number: '17134876',
    },
    colors: {
      primary: '#008B8B',      // Strider Teal
      primaryDark: '#006666',
      primaryLight: '#20B2AA',
      secondary: '#2A2A2A',    // Charcoal
      accent: '#7A8B8B',       // Slate
    },
    css: {
      primaryBg: 'bg-strider-teal',
      primaryBgHover: 'hover:bg-teal-700',
      primaryText: 'text-strider-teal',
      primaryBorder: 'border-strider-teal',
      gradientFrom: 'from-strider-teal',
      gradientTo: 'to-teal-700',
      headingText: 'text-strider-charcoal',
      ringColor: 'ring-strider-teal',
      focusRing: 'focus:ring-strider-teal',
    },
    heroTitle: "Don't gamble on bad Wi-Fi.",
    heroSubtitle: "Discover the UK's best cafes, pubs, and hotels for remote working.",
    welcomeTitle: "Let's expand where the world can succeed.",
    welcomeSubtitle: "Verified WiFi speeds · Power outlets · Work-friendly venues",
    metaTitle: "Strider | Find Verified Workspaces in UK",
    metaDescription: "Discover verified cafes, hotels, libraries and coworking spaces with measured WiFi speeds and power outlets for remote work across the UK.",
  },
  wafw: {
    id: 'wafw',
    name: 'WAFW',
    fullName: 'Work Away From Work',
    tagline: 'Work Anywhere Worth Working',
    logo: '/wafw-logo.png',
    domain: 'wafw.net',
    email: 'info@wafw.net',
    social: {
      instagram: null,
      facebook: null,
    },
    company: {
      name: 'BOLDHORN PRESS LTD',
      number: '17134876',
    },
    colors: {
      primary: '#2563EB',      // Blue
      primaryDark: '#1D4ED8',
      primaryLight: '#3B82F6',
      secondary: '#1E293B',    // Slate
      accent: '#8B5CF6',       // Purple
    },
    css: {
      primaryBg: 'bg-blue-600',
      primaryBgHover: 'hover:bg-blue-700',
      primaryText: 'text-blue-600',
      primaryBorder: 'border-blue-600',
      gradientFrom: 'from-blue-600',
      gradientTo: 'to-blue-800',
      headingText: 'text-gray-900',
      ringColor: 'ring-blue-600',
      focusRing: 'focus:ring-blue-500',
    },
    heroTitle: "Don't gamble on bad Wi-Fi.",
    heroSubtitle: "Discover the UK's best cafes, pubs, and hotels for remote working.",
    welcomeTitle: "Work Anywhere Worth Working",
    welcomeSubtitle: "Find the perfect spot to plug in and get things done",
    metaTitle: "Work Away From Work | Find Remote Workspaces in UK",
    metaDescription: "Discover the best cafes, hotels, libraries and coworking spaces with fast WiFi and power outlets for remote work across the UK.",
  }
};

// Detect brand from hostname
function detectBrand() {
  if (typeof window === 'undefined') return 'strider';
  
  const hostname = window.location.hostname.toLowerCase();
  
  // Check for WAFW domains
  if (hostname.includes('wafw') || hostname.includes('workawayfromwork')) {
    return 'wafw';
  }
  
  // Check for Strider domains
  if (hostname.includes('strider')) {
    return 'strider';
  }
  
  // For preview/localhost, check URL params or default to strider
  const params = new URLSearchParams(window.location.search);
  const brandParam = params.get('brand');
  if (brandParam && BRANDS[brandParam]) {
    return brandParam;
  }
  
  // Default to Strider for new deployments
  return 'strider';
}

const BrandContext = createContext(null);

export function BrandProvider({ children }) {
  const brand = useMemo(() => {
    const brandId = detectBrand();
    return BRANDS[brandId];
  }, []);

  return (
    <BrandContext.Provider value={brand}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}

export { BRANDS };
